import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, MapPin, MessageCircle, Phone, CheckCircle2, Calendar, Mountain, Clock, Sparkles } from 'lucide-react';
import { SUB_REGIONS, COMPANY, HIMACHAL_TOURS } from '../mock';
import { Button } from '../components/ui/button';
import EnquiryForm from '../components/sections/EnquiryForm';
import { CTABanner } from '../components/sections/CTASections';
import PerfectDuration from '../components/sections/PerfectDuration';
import SummerOffers from '../components/sections/SummerOffers';
import { AllInclusiveGrid, ExperienceGallery, TrustBanner } from '../components/sections/EngagementSections';
import TravelerTypes from '../components/sections/TravelerTypes';
import HeroSection from '../components/sections/HeroSection';
import ReviewCards from '../components/sections/ReviewCards';
import CustomerVideoReviews from '../components/sections/CustomerVideoReviews';
import { DESTINATION_SPECIFIC_DATA } from '../constants/destinationData';
import { getVideoReviews } from '../constants/videoReviews';


export function SubRegionPage() {
  const { slug } = useParams();
  const region = SUB_REGIONS.find(r => r.slug === slug);
  if (!region) return (
    <div className="max-w-7xl mx-auto px-6 py-20 text-center text-brand-blue">
      Region not found. <Link to="/destinations" className="text-brand-orange underline">Back to destinations</Link>
    </div>
  );
  const others = SUB_REGIONS.filter(r => r.slug !== slug);
  // Show matching Himachal tours if region is in HP
  const relatedTours = region.state === 'Himachal Pradesh' ? HIMACHAL_TOURS.filter(t => t.name.toLowerCase().includes(region.name.toLowerCase())).slice(0, 3) : [];

  const hasExtraData = !!DESTINATION_SPECIFIC_DATA[slug];

  const META_BY_SLUG = {
    'shimla': { title: 'Shimla Packages - Weekend & Family Tours - Custom Itinerary Free - Budget To Luxury', desc: 'Weekend & Family Tours. Custom Itinerary Free. 2 To 10 Day Tours. Budget To Luxury Trips.' },
    'manali': { title: 'Manali Packages - Lowest Price Guaranteed - Verified Hotels Only - 2 To 10 Day Packages', desc: 'Lowest Price Guaranteed. Verified Hotels Only. 2 To 10 Day Packages. Find Your Ideal Tour.' },
  };
  const meta = META_BY_SLUG[slug];

  return (
    <div>
      <Helmet>
        <title>{meta ? meta.title : `${region.name} Tours & Travel | Mountains Chain Travel`}</title>
        <meta name="description" content={meta ? meta.desc : region.tagline} />
        <meta property="og:title" content={meta ? meta.title : `${region.name} Tours & Travel | Mountains Chain Travel`} />
        <meta property="og:description" content={meta ? meta.desc : `${region.name} tours & travel packages. ${region.tagline} Book customizable trips with Mountains Chain Travel.`} />
      </Helmet>
      <HeroSection slug={slug} name={region.name} />
      <ReviewCards destinationKey={slug} />
      <CustomerVideoReviews reviews={getVideoReviews(slug)} />
      
      <TrustBanner />

      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-brand-orange/10 text-brand-orange rounded-full text-[12px] font-bold uppercase tracking-wider">
              <MapPin size={13}/>{region.state}
            </div>
            <h2 className="font-poppins text-[28px] text-brand-blue mt-4 mb-3">About {region.name}</h2>
            <p className="text-brand-blue/75 text-[15.5px] leading-[1.75]">{region.hero}</p>

            <div className="grid sm:grid-cols-3 gap-3 mt-7">
              {[
                { icon: Calendar, label: 'Best Time', value: region.bestTime },
                { icon: Mountain, label: 'Altitude', value: region.altitude },
                { icon: Clock, label: 'Ideal Duration', value: region.duration },
              ].map((x, i) => (
                <div key={i} className="bg-brand-grey rounded-xl p-4">
                  <div className="flex items-center gap-2 text-brand-orange text-[11px] font-bold tracking-wider uppercase"><x.icon size={13}/>{x.label}</div>
                  <div className="text-brand-blue text-[14px] font-semibold mt-1.5">{x.value}</div>
                </div>
              ))}
            </div>

            <h3 className="font-poppins text-[20px] text-brand-blue mt-10 mb-3">Places to explore</h3>
            <div className="flex flex-wrap gap-2">
              {region.places.map(p => (
                <span key={p} className="px-3 py-1.5 bg-white border border-brand-greyDark rounded-full text-[13px] text-brand-blue font-medium flex items-center gap-1.5 hover:border-brand-orange hover:text-brand-orange transition-colors">
                  <MapPin size={12} className="text-brand-orange"/>{p}
                </span>
              ))}
            </div>

            <h3 className="font-poppins text-[20px] text-brand-blue mt-10 mb-4 flex items-center gap-2"><Sparkles size={18} className="text-brand-orange"/>Top experiences</h3>
            <ul className="space-y-3">
              {region.highlights.map((h, i) => (
                <li key={i} className="flex items-start gap-3 text-brand-blue/85 text-[14.5px] leading-relaxed">
                  <span className="w-6 h-6 rounded-full bg-brand-orange/10 text-brand-orange flex items-center justify-center shrink-0 mt-0.5 text-[11px] font-bold">{i+1}</span>{h}
                </li>
              ))}
            </ul>

            <div className="mt-8 p-5 rounded-2xl bg-brand-blue text-white">
              <div className="flex items-center gap-2 text-brand-orange text-[11px] font-bold tracking-wider uppercase mb-1"><Sparkles size={13}/>Local Tip</div>
              <p className="text-[14px] leading-relaxed text-white/85">Our team has personally explored {region.name} dozens of times. Tell us what you love - photography, food, treks, monasteries - and we will hand-craft a route around it. No two travelers get the same itinerary.</p>
            </div>
          </div>

          <aside className="bg-white border border-brand-greyDark rounded-2xl p-6 h-fit lg:sticky lg:top-24 shadow-card">
            <div className="flex items-center gap-2 text-brand-orange text-[11px] font-bold uppercase tracking-wider mb-3"><Clock size={14}/>Plan Your Trip</div>
            <p className="text-brand-blue/70 text-[13.5px] mb-4">Get a custom itinerary in minutes. Free, no obligation - a real travel designer replies on WhatsApp.</p>
            <div className="flex flex-col gap-2.5 mt-2">
              <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Hi Mountains Chain Travel! 👋\n\nI\'m planning a trip to ' + region.name + ' (' + region.state + ') and would love a customized itinerary.\n\nPlease share:\n- Available tour packages\n- Best time to visit\n- Must-see attractions\n\nThank you!')}`} target="_blank" rel="noreferrer">
                <Button className="w-full bg-brand-orange hover:bg-brand-orangeDark text-white h-11 rounded-xl btn-glow"><MessageCircle size={16} className="mr-2"/>Get Custom Quote</Button>
              </a>
              <a href={`tel:${COMPANY.phone}`}>
                <Button variant="outline" className="w-full border-brand-blue/20 text-brand-blue hover:bg-brand-blue hover:text-white h-11 rounded-xl"><Phone size={16} className="mr-2"/>Call to Customize</Button>
              </a>
              <Link to={region.parent}>
                <Button variant="outline" className="w-full border-brand-greyDark text-brand-blue/70 hover:bg-brand-grey h-10 rounded-xl text-[13px]"><ArrowRight size={14} className="mr-1.5"/>View {region.state} portal</Button>
              </Link>
            </div>
            <div className="mt-5 pt-5 border-t border-brand-grey space-y-2 text-[12.5px] text-brand-blue/75">
              <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-orange mt-0.5"/>Local guides who live here</div>
              <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-orange mt-0.5"/>Hand-picked verified stays</div>
              <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-orange mt-0.5"/>Permits & paperwork handled</div>
              <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-orange mt-0.5"/>Free cancellation up to 14 days</div>
            </div>
          </aside>
        </div>

        {relatedTours.length > 0 && (
          <div className="mt-16">
            <h2 className="font-poppins text-[26px] text-brand-blue mb-6">{region.name} tour packages</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedTours.map(t => (
                <Link key={t.slug} to={`/himachal-tours/${t.slug}`} className="group rounded-2xl overflow-hidden bg-white border border-brand-greyDark shadow-card card-hover">
                  <div className="aspect-[4/3] img-zoom-wrap overflow-hidden"><img src={t.image} alt={t.name} className="w-full h-full object-cover img-zoom"/></div>
                  <div className="p-4">
                    <h3 className="font-poppins font-bold text-brand-blue text-[16px] group-hover:text-brand-orange transition-colors">{t.name}</h3>
                    <div className="text-[12px] text-brand-blue/60 mt-1">{t.nights}N / {t.days}D · From ₹{t.price.toLocaleString('en-IN')}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-16">
          <h2 className="font-poppins text-[26px] text-brand-blue mb-6">Explore more regions</h2>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {others.map(r => (
              <Link key={r.slug} to={`/explore/${r.slug}`} className="group rounded-xl overflow-hidden img-zoom-wrap relative h-40 block">
                <img src={r.image} alt={r.name} className="absolute inset-0 w-full h-full object-cover img-zoom"/>
                <div className="absolute inset-0 bg-gradient-to-t from-brand-blue to-transparent"/>
                <div className="relative h-full flex flex-col justify-end p-3 text-white">
                  <div className="text-[10.5px] text-white/75 uppercase tracking-wider">{r.state}</div>
                  <h4 className="font-poppins font-bold text-[15px] leading-tight">{r.name}</h4>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {hasExtraData && (
        <>
          <TravelerTypes slug={slug} />
          <div id={slug === 'manali' ? 'manali-trip-duration' : slug === 'shimla' ? 'shimla-packages' : undefined}><PerfectDuration slug={slug} /></div>
          <div id={slug === 'shimla' ? 'shimla-stay' : slug === 'manali' ? 'manali-packages' : undefined}><AllInclusiveGrid slug={slug} /></div>
          <ExperienceGallery slug={slug} />
          <SummerOffers slug={slug} />
        </>
      )}

      <CTABanner/>
      <EnquiryForm/>
    </div>
  );
}


